import React from 'react';
import { User } from 'lucide-react';
import useBlockStyles from '../common/useBlockStyles';

const STYLE_OPTIONS = {
  defaultBackground: 'section',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'center',
  defaultPadding: 'normal',
};

const TestimonialCard = ({ item, styles }) => {
  return (
    <div
      className="group relative h-full rounded-2xl overflow-hidden text-center"
      style={{
        backgroundColor: styles.cardBackground,
        border: `1px solid ${styles.accentColor}35`,
        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        transition: 'transform 0.4s, box-shadow 0.4s, border-color 0.4s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = `0 20px 40px rgba(0,0,0,0.25), 0 0 0 1px ${styles.accentColor}20`;
        e.currentTarget.style.border = `1px solid ${styles.accentColor}60`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
        e.currentTarget.style.border = `1px solid ${styles.accentColor}35`;
      }}
    >

      <div className="px-6 py-8 md:px-8 md:py-10 flex flex-col h-full">
        {/* Avatar */}
        <div className="flex justify-center mb-5">
          {item.avatar ? (
            <img
              src={item.avatar}
              alt={item.author || ''}
              className="w-16 h-16 rounded-full object-cover"
              style={{
                boxShadow: `0 0 0 3px ${styles.cardBackground}, 0 0 0 4px ${styles.accentColor}25, 0 8px 24px rgba(0,0,0,0.2)`,
              }}
            />
          ) : (
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{
                background: `linear-gradient(135deg, ${styles.accentColor}15, ${styles.accentColor}08)`,
                boxShadow: `0 0 0 3px ${styles.cardBackground}, 0 0 0 4px ${styles.accentColor}15`,
              }}
            >
              <User size={22} style={{ color: styles.accentColor }} className="opacity-40" />
            </div>
          )}
        </div>

        {/* Name */}
        {item.author && (
          <h4
            className="text-base font-bold mb-1"
            style={{ color: styles.headingColor }}
          >
            {item.author}
          </h4>
        )}

        {/* Role */}
        {item.role && (
          <p className="text-xs mb-5" style={{ color: styles.mutedColor }}>
            {item.role}
          </p>
        )}

        {/* Divider */}
        <div
          className="w-8 h-px mx-auto mb-5"
          style={{ backgroundColor: `${styles.accentColor}20` }}
        />

        {/* Quote */}
        <p
          className="text-sm leading-[1.8] italic flex-grow whitespace-pre-wrap"
          style={{ color: `${styles.bodyColor}cc`, fontFamily: styles.bodyFontStyle }}
        >
          &ldquo;{item.quote}&rdquo;
        </p>
      </div>
    </div>
  );
};

const TestimonialsRenderer = ({ block, theme = {} }) => {
  const { content } = block;
  const { heading, items = [] } = content;

  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);

  const validItems = items.filter((item) => item.quote?.trim());

  if (validItems.length === 0) {
    return (
      <div
        className={`w-full ${styles.sectionPaddingClass}`}
        style={{ backgroundColor: styles.sectionBackground }}
      >
        <div className="max-w-6xl mx-auto text-center" style={{ color: styles.mutedColor }}>
          <p className="text-sm">No testimonials added yet.</p>
        </div>
      </div>
    );
  }

  const getGridClass = () => {
    if (validItems.length === 1) return 'grid-cols-1 max-w-md mx-auto';
    if (validItems.length === 2) return 'grid-cols-1 sm:grid-cols-2 max-w-3xl mx-auto';
    if (validItems.length <= 4) return 'grid-cols-1 sm:grid-cols-2 max-w-4xl mx-auto';
    return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto';
  };

  return (
    <div
      className={`w-full ${styles.sectionPaddingClass}`}
      style={{ backgroundColor: styles.sectionBackground }}
    >
      <div className="max-w-6xl mx-auto">
        {heading && (
          <h3
            className={`${styles.headingSizeClass} font-bold text-center mb-8 sm:mb-10 md:mb-12`}
            style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
          >
            {heading}
          </h3>
        )}

        <div className={`grid gap-6 ${getGridClass()}`}>
          {validItems.map((item) => (
            <TestimonialCard key={item.id} item={item} styles={styles} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default TestimonialsRenderer;
